import React, { useMemo, useState } from 'react';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import { FileText, Download, Plus, Calendar, Search, Filter, MoreHorizontal, Clock, CheckCircle2, AlertTriangle, Loader2, Trash2, Pencil, Copy, ArrowUpRight, ArrowDownRight, Sparkles, X, Eye } from 'lucide-react';
import { Widget } from '../Widget';
import { KpiCard } from '../KpiCard';
export type AnalyticsSection = 'overview' | 'report';
type Props = {
  section: AnalyticsSection;
};
export function Analytics({
  section
}: Props) {
  if (section === 'report') return <ReportSection data-id="element-854" />;
  return <OverviewSection data-id="element-855" />;
}
// ────────────────────────────────────────────────────────────
// OVERVIEW
// ────────────────────────────────────────────────────────────
const TREND_DATA = [{
  day: '01',
  spedizioni: 124,
  anomalie: 4
}, {
  day: '04',
  spedizioni: 142,
  anomalie: 3
}, {
  day: '07',
  spedizioni: 138,
  anomalie: 6
}, {
  day: '10',
  spedizioni: 156,
  anomalie: 2
}, {
  day: '13',
  spedizioni: 168,
  anomalie: 4
}, {
  day: '16',
  spedizioni: 152,
  anomalie: 5
}, {
  day: '19',
  spedizioni: 178,
  anomalie: 3
}, {
  day: '22',
  spedizioni: 184,
  anomalie: 2
}, {
  day: '25',
  spedizioni: 196,
  anomalie: 4
}, {
  day: '28',
  spedizioni: 208,
  anomalie: 3
}];
const CHANNEL_DATA = [{
  name: 'E-commerce',
  value: 1820,
  color: '#D4FF3A'
}, {
  name: 'Retail',
  value: 1240,
  color: '#5DA9FF'
}, {
  name: 'B2B',
  value: 542,
  color: '#F5A524'
}, {
  name: 'Marketplace',
  value: 240,
  color: '#A78BFA'
}];
const COST_DATA = [{
  mese: 'Gen',
  costo: 18420
}, {
  mese: 'Feb',
  costo: 19840
}, {
  mese: 'Mar',
  costo: 21240
}, {
  mese: 'Apr',
  costo: 22680
}, {
  mese: 'Mag',
  costo: 24120
}];
function OverviewSection() {
  const [range, setRange] = useState<'7g' | '30g' | '90g'>('30g');
  return <div className="space-y-6" data-id="element-856">
      <PageHeader title="Analytics" right={<div className="flex items-center gap-2" data-id="element-858">
            <RangePicker value={range} onChange={setRange} data-id="element-859" />
            <button type="button" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#181818] border border-[#262626] text-xs font-medium text-[#FAFAFA] hover:border-[#333] transition" data-id="element-860">
              <Download className="h-3.5 w-3.5" data-id="element-861" />
              Esporta
            </button>
          </div>} data-id="element-857" />

      {/* KPI strip */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-862">
        <KpiCard label="Spedizioni totali" value="3.842" data-id="element-863" />
        <KpiCard label="Tasso on-time" value="96.4%" data-id="element-864" />
        <KpiCard label="Costo medio" value="€ 9,82" data-id="element-865" />
        <KpiCard label="% Anomalie" value="2.6%" data-id="element-866" />
      </section>

      {/* Trend + channel split */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-id="element-867">
        <div className="lg:col-span-2" data-id="element-868">
          <Widget title="Volume spedizioni" headerRight={<span className="text-[10px] uppercase tracking-wider text-[#6B6B6B] font-medium" data-id="element-870">
                Ultimi 30 giorni
              </span>} data-id="element-869">
            <div className="h-64 -mx-2" data-id="element-871">
              <ResponsiveContainer width="100%" height="100%" data-id="element-872">
                <AreaChart data={TREND_DATA} margin={{
                top: 8,
                right: 8,
                bottom: 0,
                left: -16
              }} data-id="element-873">
                  <defs data-id="element-874">
                    <linearGradient id="gradSped" x1="0" y1="0" x2="0" y2="1" data-id="element-875">
                      <stop offset="0%" stopColor="#D4FF3A" stopOpacity={0.3} data-id="element-876" />
                      <stop offset="100%" stopColor="#D4FF3A" stopOpacity={0} data-id="element-877" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#262626" vertical={false} data-id="element-878" />
                  <XAxis dataKey="day" stroke="#6B6B6B" tick={{
                  fontSize: 11
                }} axisLine={false} tickLine={false} data-id="element-879" />
                  <YAxis stroke="#6B6B6B" tick={{
                  fontSize: 11
                }} axisLine={false} tickLine={false} data-id="element-880" />
                  <Tooltip contentStyle={{
                  background: '#0A0A0A',
                  border: '1px solid #262626',
                  borderRadius: 8,
                  fontSize: 12
                }} labelStyle={{
                  color: '#9A9A9A'
                }} data-id="element-881" />
                  <Area type="monotone" dataKey="spedizioni" stroke="#D4FF3A" strokeWidth={2} fill="url(#gradSped)" data-id="element-882" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Widget>
        </div>
        <Widget title="Per canale" data-id="element-883">
          <div className="h-48 -mx-2 flex items-center justify-center" data-id="element-884">
            <ResponsiveContainer width="100%" height="100%" data-id="element-885">
              <PieChart data-id="element-886">
                <Pie data={CHANNEL_DATA} innerRadius={45} outerRadius={70} paddingAngle={2} dataKey="value" stroke="none" data-id="element-887">
                  {CHANNEL_DATA.map(c => <Cell key={c.name} fill={c.color} data-id="element-888" />)}
                </Pie>
                <Tooltip contentStyle={{
                background: '#0A0A0A',
                border: '1px solid #262626',
                borderRadius: 8,
                fontSize: 12
              }} data-id="element-889" />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <ul className="space-y-1.5 mt-3" data-id="element-890">
            {CHANNEL_DATA.map(c => <li key={c.name} className="flex items-center justify-between text-xs" data-id="element-891">
                <span className="flex items-center gap-2 text-[#9A9A9A]" data-id="element-892">
                  <span className="h-2 w-2 rounded-full" style={{
                background: c.color
              }} data-id="element-893" />
                  {c.name}
                </span>
                <span className="font-mono text-[#FAFAFA]" data-id="element-894">
                  {c.value.toLocaleString('it-IT')}
                </span>
              </li>)}
          </ul>
        </Widget>
      </section>

      {/* Cost trend + top destinations */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-895">
        <Widget title="Spesa logistica mensile" headerRight={<span className="text-xs text-[#D4FF3A] font-mono" data-id="element-897">
              <ArrowUpRight className="h-3 w-3 inline mr-0.5" data-id="element-898" />
              +6.4%
            </span>} data-id="element-896">
          <div className="h-56 -mx-2" data-id="element-899">
            <ResponsiveContainer width="100%" height="100%" data-id="element-900">
              <BarChart data={COST_DATA} margin={{
              top: 8,
              right: 8,
              bottom: 0,
              left: -16
            }} data-id="element-901">
                <CartesianGrid stroke="#262626" vertical={false} data-id="element-902" />
                <XAxis dataKey="mese" stroke="#6B6B6B" tick={{
                fontSize: 11
              }} axisLine={false} tickLine={false} data-id="element-903" />
                <YAxis stroke="#6B6B6B" tick={{
                fontSize: 11
              }} axisLine={false} tickLine={false} data-id="element-904" />
                <Tooltip contentStyle={{
                background: '#0A0A0A',
                border: '1px solid #262626',
                borderRadius: 8,
                fontSize: 12
              }} labelStyle={{
                color: '#9A9A9A'
              }} cursor={{
                fill: '#1F1F1F'
              }} data-id="element-905" />
                <Bar dataKey="costo" fill="#D4FF3A" radius={[6, 6, 0, 0]} data-id="element-906" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Widget>

        <Widget title="Top destinazioni" bodyPadding="none" data-id="element-907">
          <ul className="divide-y divide-[#262626]" data-id="element-908">
            {[{
            city: 'Milano',
            shipments: 842,
            share: 22,
            trend: +4
          }, {
            city: 'Roma',
            shipments: 612,
            share: 16,
            trend: +2
          }, {
            city: 'Torino',
            shipments: 408,
            share: 11,
            trend: +1
          }, {
            city: 'Napoli',
            shipments: 384,
            share: 10,
            trend: -2
          }, {
            city: 'Bologna',
            shipments: 312,
            share: 8,
            trend: +3
          }].map(row => <li key={row.city} className="flex items-center justify-between px-5 py-3" data-id="element-909">
                <div className="min-w-0 flex-1" data-id="element-910">
                  <div className="text-sm text-[#FAFAFA]" data-id="element-911">{row.city}</div>
                  <div className="mt-1.5 h-1 w-full bg-[#0A0A0A] rounded-full overflow-hidden" data-id="element-912">
                    <div className="h-full bg-[#D4FF3A]" style={{
                  width: `${row.share * 4}%`
                }} data-id="element-913" />
                  </div>
                </div>
                <div className="ml-4 text-right shrink-0" data-id="element-914">
                  <div className="text-sm font-mono text-[#FAFAFA]" data-id="element-915">
                    {row.shipments}
                  </div>
                  <div className={`text-[10px] font-mono ${row.trend >= 0 ? 'text-[#D4FF3A]' : 'text-[#EF4444]'}`} data-id="element-916">
                    {row.trend >= 0 ? '+' : ''}
                    {row.trend}%
                  </div>
                </div>
              </li>)}
          </ul>
        </Widget>
      </section>
    </div>;
}
// ────────────────────────────────────────────────────────────
// REPORT
// ────────────────────────────────────────────────────────────
type ReportItem = {
  id: string;
  title: string;
  description: string;
  category: 'Operations' | 'Finanza' | 'Qualità' | 'Corrieri';
  schedule: string;
  lastRun: string;
  format: 'PDF' | 'XLSX' | 'CSV';
};
const REPORTS: ReportItem[] = [{
  id: 'rpt-001',
  title: 'Performance corrieri mensile',
  description: 'KPI per corriere: on-time, anomalie, resi e costo medio. Confronto con il mese precedente.',
  category: 'Corrieri',
  schedule: 'Mensile · 1° del mese',
  lastRun: '01 mag 2026 · 08:00',
  format: 'PDF'
}, {
  id: 'rpt-002',
  title: 'Anomalie settimanali per negozio',
  description: 'Lista delle anomalie raggruppate per negozio destinatario e tipologia.',
  category: 'Qualità',
  schedule: 'Settimanale · Lunedì',
  lastRun: '19 mag 2026 · 09:00',
  format: 'XLSX'
}, {
  id: 'rpt-003',
  title: 'Spesa logistica per centro di costo',
  description: 'Allocazione della spesa per centro di costo, sucursal e canale di vendita.',
  category: 'Finanza',
  schedule: 'Mensile · 5 del mese',
  lastRun: '05 mag 2026 · 07:30',
  format: 'XLSX'
}, {
  id: 'rpt-004',
  title: 'Resi e cause root',
  description: 'Resi nel periodo con suddivisione per causa, corriere e tempo medio di processing.',
  category: 'Operations',
  schedule: 'Mensile',
  lastRun: '01 mag 2026 · 08:00',
  format: 'PDF'
}, {
  id: 'rpt-005',
  title: 'Ritardi e SLA breach',
  description: 'Spedizioni in ritardo e violazioni SLA per tratta, con impatto economico stimato.',
  category: 'Qualità',
  schedule: 'Settimanale',
  lastRun: '19 mag 2026 · 09:00',
  format: 'CSV'
}, {
  id: 'rpt-006',
  title: 'Volumi per fascia oraria',
  description: 'Distribuzione ritiri e consegne per fascia oraria, utile per il dimensionamento.',
  category: 'Operations',
  schedule: 'Trimestrale',
  lastRun: '01 apr 2026 · 08:00',
  format: 'XLSX'
}];
const CATEGORIES = ['Tutti', 'Operations', 'Finanza', 'Qualità', 'Corrieri'] as const;
function ReportSection() {
  const [reports, setReports] = useState<ReportItem[]>(REPORTS);
  const [category, setCategory] = useState<(typeof CATEGORIES)[number]>('Tutti');
  const [query, setQuery] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [previewReport, setPreviewReport] = useState<ReportItem | null>(null);
  const filtered = reports.filter(r => {
    if (category !== 'Tutti' && r.category !== category) return false;
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return `${r.title} ${r.description} ${r.category}`.toLowerCase().includes(q);
  });
  const handleDelete = (id: string) => {
    setReports(prev => prev.filter(r => r.id !== id));
  };
  return <div className="space-y-6" data-id="element-917">
      <PageHeader title="Report" right={<button type="button" onClick={() => setIsCreating(true)} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-[#D4FF3A] text-[#0A0A0A] text-sm font-semibold hover:bg-[#c2ed28] transition" data-id="element-919">
            <Plus className="h-4 w-4" data-id="element-920" />
            Nuovo report
          </button>} data-id="element-918" />

      <div className="flex items-center gap-3 flex-wrap" data-id="element-921">
        <div className="relative flex-1 min-w-[240px] max-w-md" data-id="element-922">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B6B6B] pointer-events-none" data-id="element-923" />
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Cerca report..." className="w-full pl-9 pr-3 py-2 bg-[#181818] border border-[#262626] rounded-lg text-sm text-[#FAFAFA] placeholder:text-[#6B6B6B] focus:outline-none focus:border-[#D4FF3A]/40" data-id="element-924" />
        </div>
        <div className="flex items-center gap-1 p-1 rounded-lg bg-[#181818] border border-[#262626] overflow-x-auto" data-id="element-925">
          {CATEGORIES.map(c => <button key={c} type="button" onClick={() => setCategory(c)} className={`px-2.5 py-1 rounded-md text-xs font-medium transition whitespace-nowrap ${category === c ? 'bg-[#0A0A0A] text-[#FAFAFA]' : 'text-[#9A9A9A] hover:text-[#FAFAFA]'}`} data-id="element-926">
              {c}
            </button>)}
        </div>
      </div>

      <div className="bg-[#181818] border border-[#262626] rounded-xl overflow-hidden overflow-x-auto" data-id="element-927">
        <table className="w-full text-sm text-left whitespace-nowrap" data-id="element-928">
          <thead className="text-[10px] uppercase tracking-wider text-[#6B6B6B] border-b border-[#262626] bg-[#0A0A0A]" data-id="element-929">
            <tr data-id="element-930">
              <th className="px-5 py-3 font-medium" data-id="element-931">ID</th>
              <th className="px-5 py-3 font-medium" data-id="element-932">Titolo</th>
              <th className="px-5 py-3 font-medium" data-id="element-933">Categoria</th>
              <th className="px-5 py-3 font-medium" data-id="element-934">Schedule</th>
              <th className="px-5 py-3 font-medium" data-id="element-935">Ultimo run</th>
              <th className="px-5 py-3 font-medium" data-id="element-936">Formato</th>
              <th className="px-5 py-3 font-medium text-right" data-id="element-937">Azioni</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#1F1F1F]" data-id="element-938">
            {filtered.map(r => <tr key={r.id} className="hover:bg-[#0A0A0A] transition-colors group" data-id="element-939">
                <td className="px-5 py-3.5" data-id="element-940">
                  <span className="font-mono text-xs text-[#9A9A9A]" data-id="element-941">
                    {r.id}
                  </span>
                </td>
                <td className="px-5 py-3.5 min-w-[250px] whitespace-normal" data-id="element-942">
                  <div className="font-medium text-[#FAFAFA]" data-id="element-943">{r.title}</div>
                  <div className="text-xs text-[#6B6B6B] mt-0.5 line-clamp-1" data-id="element-944">
                    {r.description}
                  </div>
                </td>
                <td className="px-5 py-3.5" data-id="element-945">
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-semibold bg-[#0A0A0A] border border-[#262626] text-[#9A9A9A]" data-id="element-946">
                    {r.category}
                  </span>
                </td>
                <td className="px-5 py-3.5 text-[#FAFAFA]" data-id="element-947">{r.schedule}</td>
                <td className="px-5 py-3.5" data-id="element-948">
                  <span className="font-mono text-xs text-[#9A9A9A]" data-id="element-949">
                    {r.lastRun}
                  </span>
                </td>
                <td className="px-5 py-3.5" data-id="element-950">
                  <span className="font-mono text-xs text-[#9A9A9A]" data-id="element-951">
                    {r.format}
                  </span>
                </td>
                <td className="px-5 py-3.5 text-right" data-id="element-952">
                  <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-953">
                    <button type="button" onClick={() => setPreviewReport(r)} className="p-1.5 rounded-lg text-[#9A9A9A] hover:text-[#FAFAFA] hover:bg-[#262626] transition" title="Anteprima" data-id="element-954">
                      <Eye className="h-4 w-4" data-id="element-955" />
                    </button>
                    <button type="button" className="p-1.5 rounded-lg text-[#9A9A9A] hover:text-[#FAFAFA] hover:bg-[#262626] transition" title="Scarica" data-id="element-956">
                      <Download className="h-4 w-4" data-id="element-957" />
                    </button>
                    <button type="button" className="p-1.5 rounded-lg text-[#9A9A9A] hover:text-[#FAFAFA] hover:bg-[#262626] transition" title="Modifica" data-id="element-958">
                      <Pencil className="h-4 w-4" data-id="element-959" />
                    </button>
                    <button type="button" onClick={() => handleDelete(r.id)} className="p-1.5 rounded-lg text-[#9A9A9A] hover:text-[#EF4444] hover:bg-[#EF4444]/10 transition" title="Elimina" data-id="element-960">
                      <Trash2 className="h-4 w-4" data-id="element-961" />
                    </button>
                  </div>
                </td>
              </tr>)}
            {filtered.length === 0 && <tr data-id="element-962">
                <td colSpan={7} className="px-5 py-8 text-center text-sm text-[#6B6B6B]" data-id="element-963">
                  Nessun report corrisponde ai filtri.
                </td>
              </tr>}
          </tbody>
        </table>
      </div>

      {isCreating && <NewReportModal onClose={() => setIsCreating(false)} onGenerate={newReport => {
      setReports(prev => [newReport, ...prev]);
      setIsCreating(false);
      setPreviewReport(newReport);
    }} data-id="element-964" />}

      {previewReport && <ReportPreviewModal report={previewReport} onClose={() => setPreviewReport(null)} data-id="element-965" />}
    </div>;
}
function NewReportModal({
  onClose,
  onGenerate
}: {
  onClose: () => void;
  onGenerate: (report: ReportItem) => void;
}) {
  const [step, setStep] = useState(1);
  // Step 1: Dettagli
  const [category, setCategory] = useState<ReportItem['category']>('Operations');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  // Step 2: Filtri
  const [dateRange, setDateRange] = useState('Ultimi 30g');
  const [carriers, setCarriers] = useState<string[]>(['FedEx', 'BRT']);
  const [store, setStore] = useState('');
  const [costCenter, setCostCenter] = useState('');
  // Step 3: Formato e schedule
  const [format, setFormat] = useState<ReportItem['format']>('PDF');
  const [scheduleType, setScheduleType] = useState<'Una tantum' | 'Ricorrente'>('Una tantum');
  const [frequency, setFrequency] = useState('Mensile');
  const toggleCarrier = (c: string) => {
    setCarriers(prev => prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c]);
  };
  const canProceed = () => {
    if (step === 1) return title.trim().length > 0;
    if (step === 2) return carriers.length > 0;
    return true;
  };
  const handleGenerate = () => {
    const now = new Date();
    const ts = `${now.getDate()} ${['gen', 'feb', 'mar', 'apr', 'mag', 'giu', 'lug', 'ago', 'set', 'ott', 'nov', 'dic'][now.getMonth()]} ${now.getFullYear()} · ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    const newReport: ReportItem = {
      id: `rpt-${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`,
      title: title.trim(),
      description: description.trim() || 'Nessuna descrizione.',
      category,
      schedule: scheduleType === 'Una tantum' ? 'Una tantum' : `${frequency}`,
      lastRun: ts,
      format
    };
    onGenerate(newReport);
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-966">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} data-id="element-967" />
      <div className="relative w-full max-w-xl bg-[#0F0F0F] border border-[#262626] rounded-2xl overflow-hidden flex flex-col max-h-[90vh]" data-id="element-968">
        <div className="px-5 py-4 border-b border-[#262626] flex items-center justify-between shrink-0" data-id="element-969">
          <h2 className="text-base font-semibold text-[#FAFAFA] font-heading" data-id="element-970">
            Nuovo report
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg text-[#9A9A9A] hover:text-[#FAFAFA] hover:bg-[#181818] transition" data-id="element-971">
            <X className="h-4 w-4" data-id="element-972" />
          </button>
        </div>

        {/* Progress bar */}
        <div className="h-1 w-full bg-[#181818]" data-id="element-973">
          <div className="h-full bg-[#D4FF3A] transition-all duration-300" style={{
          width: `${step / 4 * 100}%`
        }} data-id="element-974" />
        </div>

        <div className="p-5 overflow-y-auto flex-1" data-id="element-975">
          <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] font-medium mb-5" data-id="element-976">
            Passaggio {step} di 4
          </div>

          {step === 1 && <div className="space-y-4" data-id="element-977">
              <div data-id="element-978">
                <label className="block text-[10px] uppercase tracking-wider text-[#9A9A9A] font-medium mb-1.5" data-id="element-979">
                  Categoria
                </label>
                <select value={category} onChange={e => setCategory(e.target.value as any)} className="w-full bg-[#0A0A0A] border border-[#262626] rounded-lg px-3 py-2 text-sm text-[#FAFAFA] focus:outline-none focus:border-[#D4FF3A]/40" data-id="element-980">
                  <option value="Operations" data-id="element-981">Operations</option>
                  <option value="Finanza" data-id="element-982">Finanza</option>
                  <option value="Qualità" data-id="element-983">Qualità</option>
                  <option value="Corrieri" data-id="element-984">Corrieri</option>
                </select>
              </div>
              <div data-id="element-985">
                <label className="block text-[10px] uppercase tracking-wider text-[#9A9A9A] font-medium mb-1.5" data-id="element-986">
                  Titolo <span className="text-[#D4FF3A]" data-id="element-987">*</span>
                </label>
                <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="Es. Performance corrieri Q1" className="w-full bg-[#0A0A0A] border border-[#262626] rounded-lg px-3 py-2 text-sm text-[#FAFAFA] focus:outline-none focus:border-[#D4FF3A]/40" data-id="element-988" />
              </div>
              <div data-id="element-989">
                <label className="block text-[10px] uppercase tracking-wider text-[#9A9A9A] font-medium mb-1.5" data-id="element-990">
                  Descrizione
                </label>
                <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Breve descrizione del contenuto del report..." rows={3} className="w-full bg-[#0A0A0A] border border-[#262626] rounded-lg px-3 py-2 text-sm text-[#FAFAFA] focus:outline-none focus:border-[#D4FF3A]/40 resize-none" data-id="element-991" />
              </div>
            </div>}

          {step === 2 && <div className="space-y-5" data-id="element-992">
              <div data-id="element-993">
                <label className="block text-[10px] uppercase tracking-wider text-[#9A9A9A] font-medium mb-2" data-id="element-994">
                  Intervallo temporale
                </label>
                <div className="flex flex-wrap gap-2" data-id="element-995">
                  {['Ultimi 7g', 'Ultimi 30g', 'Ultimi 90g', 'Personalizzato'].map(r => <button key={r} type="button" onClick={() => setDateRange(r)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition border ${dateRange === r ? 'bg-[#D4FF3A]/10 text-[#D4FF3A] border-[#D4FF3A]/30' : 'bg-[#0A0A0A] text-[#9A9A9A] border-[#262626] hover:border-[#333]'}`} data-id="element-996">
                      {r}
                    </button>)}
                </div>
              </div>
              <div data-id="element-997">
                <label className="block text-[10px] uppercase tracking-wider text-[#9A9A9A] font-medium mb-2" data-id="element-998">
                  Corrieri
                </label>
                <div className="grid grid-cols-2 gap-2" data-id="element-999">
                  {['FedEx', 'BRT', 'GLS', 'UPS', 'SDA'].map(c => {
                const active = carriers.includes(c);
                return <button key={c} type="button" onClick={() => toggleCarrier(c)} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition border text-left ${active ? 'bg-[#D4FF3A]/5 text-[#FAFAFA] border-[#D4FF3A]/40' : 'bg-[#0A0A0A] text-[#9A9A9A] border-[#262626] hover:border-[#333]'}`} data-id="element-1000">
                        <div className={`h-4 w-4 rounded flex items-center justify-center border ${active ? 'bg-[#D4FF3A] border-[#D4FF3A]' : 'border-[#3F3F3F]'}`} data-id="element-1001">
                          {active && <Check className="h-3 w-3 text-[#0A0A0A]" data-id="element-1002" />}
                        </div>
                        {c}
                      </button>;
              })}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-1003">
                <div data-id="element-1004">
                  <label className="block text-[10px] uppercase tracking-wider text-[#9A9A9A] font-medium mb-1.5" data-id="element-1005">
                    Store
                  </label>
                  <input type="text" value={store} onChange={e => setStore(e.target.value)} placeholder="Tutti" className="w-full bg-[#0A0A0A] border border-[#262626] rounded-lg px-3 py-2 text-sm text-[#FAFAFA] focus:outline-none focus:border-[#D4FF3A]/40" data-id="element-1006" />
                </div>
                <div data-id="element-1007">
                  <label className="block text-[10px] uppercase tracking-wider text-[#9A9A9A] font-medium mb-1.5" data-id="element-1008">
                    Centro di costo
                  </label>
                  <input type="text" value={costCenter} onChange={e => setCostCenter(e.target.value)} placeholder="Tutti" className="w-full bg-[#0A0A0A] border border-[#262626] rounded-lg px-3 py-2 text-sm text-[#FAFAFA] focus:outline-none focus:border-[#D4FF3A]/40" data-id="element-1009" />
                </div>
              </div>
            </div>}

          {step === 3 && <div className="space-y-6" data-id="element-1010">
              <div data-id="element-1011">
                <label className="block text-[10px] uppercase tracking-wider text-[#9A9A9A] font-medium mb-2" data-id="element-1012">
                  Formato
                </label>
                <div className="grid grid-cols-3 gap-3" data-id="element-1013">
                  {(['PDF', 'XLSX', 'CSV'] as const).map(f => <button key={f} type="button" onClick={() => setFormat(f)} className={`flex flex-col items-center justify-center gap-2 p-4 rounded-xl border transition ${format === f ? 'bg-[#D4FF3A]/5 border-[#D4FF3A]/40 text-[#D4FF3A]' : 'bg-[#0A0A0A] border-[#262626] text-[#9A9A9A] hover:border-[#333]'}`} data-id="element-1014">
                      <FileText className="h-6 w-6" data-id="element-1015" />
                      <span className="text-sm font-medium font-mono" data-id="element-1016">{f}</span>
                    </button>)}
                </div>
              </div>
              <div data-id="element-1017">
                <label className="block text-[10px] uppercase tracking-wider text-[#9A9A9A] font-medium mb-2" data-id="element-1018">
                  Pianificazione
                </label>
                <div className="flex gap-3 mb-3" data-id="element-1019">
                  {(['Una tantum', 'Ricorrente'] as const).map(t => <button key={t} type="button" onClick={() => setScheduleType(t)} className={`flex-1 py-2 rounded-lg text-sm font-medium transition border ${scheduleType === t ? 'bg-[#D4FF3A]/10 text-[#D4FF3A] border-[#D4FF3A]/30' : 'bg-[#0A0A0A] text-[#9A9A9A] border-[#262626] hover:border-[#333]'}`} data-id="element-1020">
                      {t}
                    </button>)}
                </div>
                {scheduleType === 'Ricorrente' && <select value={frequency} onChange={e => setFrequency(e.target.value)} className="w-full bg-[#0A0A0A] border border-[#262626] rounded-lg px-3 py-2 text-sm text-[#FAFAFA] focus:outline-none focus:border-[#D4FF3A]/40" data-id="element-1021">
                    <option value="Giornaliero" data-id="element-1022">Giornaliero</option>
                    <option value="Settimanale" data-id="element-1023">Settimanale</option>
                    <option value="Mensile" data-id="element-1024">Mensile</option>
                    <option value="Trimestrale" data-id="element-1025">Trimestrale</option>
                  </select>}
              </div>
            </div>}

          {step === 4 && <div className="space-y-4" data-id="element-1026">
              <div className="bg-[#0A0A0A] border border-[#262626] rounded-xl p-4 space-y-3" data-id="element-1027">
                <div data-id="element-1028">
                  <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] font-medium" data-id="element-1029">
                    Titolo e Categoria
                  </div>
                  <div className="text-sm font-medium text-[#FAFAFA] mt-0.5" data-id="element-1030">
                    {title} <span className="text-[#6B6B6B]" data-id="element-1031">({category})</span>
                  </div>
                </div>
                <div data-id="element-1032">
                  <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] font-medium" data-id="element-1033">
                    Filtri
                  </div>
                  <div className="text-sm text-[#FAFAFA] mt-0.5" data-id="element-1034">
                    {dateRange} · {carriers.join(', ')}
                    {store && ` · Store: ${store}`}
                    {costCenter && ` · CC: ${costCenter}`}
                  </div>
                </div>
                <div data-id="element-1035">
                  <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] font-medium" data-id="element-1036">
                    Formato e Schedule
                  </div>
                  <div className="text-sm text-[#FAFAFA] mt-0.5" data-id="element-1037">
                    {format} ·{' '}
                    {scheduleType === 'Una tantum' ? 'Una tantum' : frequency}
                  </div>
                </div>
              </div>
              <p className="text-xs text-[#9A9A9A] text-center" data-id="element-1038">
                Il report verrà generato con i dati attuali.
              </p>
            </div>}
        </div>

        <div className="border-t border-[#262626] px-5 py-4 flex items-center justify-between shrink-0 bg-[#0F0F0F]" data-id="element-1039">
          <button type="button" onClick={() => step > 1 ? setStep(step - 1) : onClose()} className="px-4 py-2 rounded-lg text-sm font-medium text-[#9A9A9A] hover:text-[#FAFAFA] transition" data-id="element-1040">
            {step === 1 ? 'Annulla' : 'Indietro'}
          </button>
          {step < 4 ? <button type="button" onClick={() => setStep(step + 1)} disabled={!canProceed()} className="px-4 py-2 rounded-lg bg-[#FAFAFA] text-[#0A0A0A] text-sm font-semibold hover:bg-white transition disabled:opacity-50" data-id="element-1041">
              Avanti
            </button> : <button type="button" onClick={handleGenerate} className="px-4 py-2 rounded-lg bg-[#D4FF3A] text-[#0A0A0A] text-sm font-semibold hover:bg-[#c2ed28] transition" data-id="element-1042">
              Genera report
            </button>}
        </div>
      </div>
    </div>;
}
function ReportPreviewModal({
  report,
  onClose
}: {
  report: ReportItem;
  onClose: () => void;
}) {
  const handlePrint = () => {
    window.print();
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-1043">
      <style media="print" data-id="element-1044">{`
        body * { visibility: hidden; }
        #report-printable, #report-printable * { visibility: visible; }
        #report-printable { position: absolute; left: 0; top: 0; width: 100%; }
      `}</style>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} data-id="element-1045" />
      <div className="relative w-full max-w-4xl bg-[#0F0F0F] border border-[#262626] rounded-2xl overflow-hidden flex flex-col max-h-[90vh]" data-id="element-1046">
        <div className="px-5 py-4 border-b border-[#262626] flex items-center justify-between shrink-0 bg-[#181818]" data-id="element-1047">
          <div className="flex items-center gap-2" data-id="element-1048">
            <FileText className="h-4 w-4 text-[#D4FF3A]" data-id="element-1049" />
            <h2 className="text-base font-semibold text-[#FAFAFA] font-heading" data-id="element-1050">
              Anteprima Report
            </h2>
          </div>
          <div className="flex items-center gap-2" data-id="element-1051">
            <button onClick={handlePrint} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#D4FF3A] text-[#0A0A0A] text-sm font-semibold hover:bg-[#c2ed28] transition" data-id="element-1052">
              <Download className="h-4 w-4" data-id="element-1053" />
              Scarica PDF
            </button>
            <button onClick={onClose} className="p-1.5 rounded-lg text-[#9A9A9A] hover:text-[#FAFAFA] hover:bg-[#262626] transition" data-id="element-1054">
              <X className="h-4 w-4" data-id="element-1055" />
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto flex-1 bg-[#0A0A0A]" id="report-printable" data-id="element-1056">
          {/* Report Header */}
          <div className="mb-8" data-id="element-1057">
            <div className="flex items-start justify-between mb-4" data-id="element-1058">
              <div data-id="element-1059">
                <h1 className="text-3xl font-bold text-[#FAFAFA] font-heading tracking-tight mb-2" data-id="element-1060">
                  {report.title}
                </h1>
                <p className="text-sm text-[#9A9A9A] max-w-2xl" data-id="element-1061">
                  {report.description}
                </p>
              </div>
              <div className="text-right" data-id="element-1062">
                <div className="inline-flex items-center px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-semibold bg-[#181818] border border-[#262626] text-[#9A9A9A] mb-2" data-id="element-1063">
                  {report.category}
                </div>
                <div className="text-xs font-mono text-[#6B6B6B]" data-id="element-1064">
                  Generato il: {report.lastRun}
                </div>
              </div>
            </div>
          </div>

          {/* KPIs */}
          <div className="grid grid-cols-4 gap-4 mb-8" data-id="element-1065">
            <div className="bg-[#181818] border border-[#262626] rounded-xl p-4" data-id="element-1066">
              <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] font-medium mb-1" data-id="element-1067">
                Spedizioni
              </div>
              <div className="text-2xl font-semibold text-[#FAFAFA] font-mono" data-id="element-1068">
                842
              </div>
            </div>
            <div className="bg-[#181818] border border-[#262626] rounded-xl p-4" data-id="element-1069">
              <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] font-medium mb-1" data-id="element-1070">
                On-time %
              </div>
              <div className="text-2xl font-semibold text-[#D4FF3A] font-mono" data-id="element-1071">
                96.4%
              </div>
            </div>
            <div className="bg-[#181818] border border-[#262626] rounded-xl p-4" data-id="element-1072">
              <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] font-medium mb-1" data-id="element-1073">
                Anomalie
              </div>
              <div className="text-2xl font-semibold text-[#EF4444] font-mono" data-id="element-1074">
                24
              </div>
            </div>
            <div className="bg-[#181818] border border-[#262626] rounded-xl p-4" data-id="element-1075">
              <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] font-medium mb-1" data-id="element-1076">
                Costo medio
              </div>
              <div className="text-2xl font-semibold text-[#FAFAFA] font-mono" data-id="element-1077">
                € 8,42
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="bg-[#181818] border border-[#262626] rounded-xl overflow-hidden mb-8" data-id="element-1078">
            <table className="w-full text-sm text-left" data-id="element-1079">
              <thead className="text-[10px] uppercase tracking-wider text-[#6B6B6B] border-b border-[#262626] bg-[#0A0A0A]" data-id="element-1080">
                <tr data-id="element-1081">
                  <th className="px-4 py-3 font-medium" data-id="element-1082">Corriere</th>
                  <th className="px-4 py-3 font-medium text-right" data-id="element-1083">Volumi</th>
                  <th className="px-4 py-3 font-medium text-right" data-id="element-1084">On-time</th>
                  <th className="px-4 py-3 font-medium text-right" data-id="element-1085">Anomalie</th>
                  <th className="px-4 py-3 font-medium text-right" data-id="element-1086">
                    Costo medio
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#1F1F1F]" data-id="element-1087">
                <tr className="hover:bg-[#0A0A0A]" data-id="element-1088">
                  <td className="px-4 py-3 font-medium text-[#FAFAFA]" data-id="element-1089">
                    FedEx
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-[#9A9A9A]" data-id="element-1090">
                    412
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-[#D4FF3A]" data-id="element-1091">
                    98.1%
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-[#9A9A9A]" data-id="element-1092">
                    8
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-[#9A9A9A]" data-id="element-1093">
                    € 9,10
                  </td>
                </tr>
                <tr className="hover:bg-[#0A0A0A]" data-id="element-1094">
                  <td className="px-4 py-3 font-medium text-[#FAFAFA]" data-id="element-1095">BRT</td>
                  <td className="px-4 py-3 text-right font-mono text-[#9A9A9A]" data-id="element-1096">
                    280
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-[#D4FF3A]" data-id="element-1097">
                    95.4%
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-[#9A9A9A]" data-id="element-1098">
                    12
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-[#9A9A9A]" data-id="element-1099">
                    € 7,80
                  </td>
                </tr>
                <tr className="hover:bg-[#0A0A0A]" data-id="element-1100">
                  <td className="px-4 py-3 font-medium text-[#FAFAFA]" data-id="element-1101">GLS</td>
                  <td className="px-4 py-3 text-right font-mono text-[#9A9A9A]" data-id="element-1102">
                    150
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-[#F5A524]" data-id="element-1103">
                    92.8%
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-[#9A9A9A]" data-id="element-1104">
                    4
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-[#9A9A9A]" data-id="element-1105">
                    € 7,20
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Notes */}
          <div className="text-xs text-[#6B6B6B] leading-relaxed" data-id="element-1106">
            <p data-id="element-1107">
              <strong data-id="element-1108">Note:</strong> I dati includono tutte le spedizioni
              completate nel periodo di riferimento. Il costo medio è calcolato
              al netto di eventuali rimborsi per ritardi o smarrimenti. Le
              anomalie conteggiate sono quelle confermate dal customer care.
            </p>
          </div>
        </div>
      </div>
    </div>;
}
// ────────────────────────────────────────────────────────────
// Shared helpers
// ────────────────────────────────────────────────────────────
function PageHeader({
  title,
  right
}: {
  title: string;
  right?: React.ReactNode;
}) {
  return <div className="flex items-center justify-between gap-3 flex-wrap" data-id="element-1109">
      <h1 className="text-2xl font-semibold text-[#FAFAFA] font-heading tracking-tight" data-id="element-1110">
        {title}
      </h1>
      {right}
    </div>;
}
function RangePicker({
  value,
  onChange
}: {
  value: '7g' | '30g' | '90g';
  onChange: (v: '7g' | '30g' | '90g') => void;
}) {
  return <div className="flex items-center gap-1 p-1 rounded-lg bg-[#181818] border border-[#262626]" data-id="element-1111">
      {(['7g', '30g', '90g'] as const).map(v => <button key={v} type="button" onClick={() => onChange(v)} className={`px-2.5 py-1 rounded-md text-xs font-medium transition ${value === v ? 'bg-[#0A0A0A] text-[#FAFAFA]' : 'text-[#9A9A9A] hover:text-[#FAFAFA]'}`} data-id="element-1112">
          {v}
        </button>)}
    </div>;
}